import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api';
import styles from '../styles/DiscordVerification.module.css';

const DiscordVerification = ({ user, onUpdate, variant = 'standalone' }) => {
  const isInline = variant === 'inline';
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [inviteUrl, setInviteUrl] = useState(null);

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  useEffect(() => {
    if (typeof api.getOnboardingConfig !== 'function') return undefined;
    let cancelled = false;
    Promise.resolve(api.getOnboardingConfig())
      .then((res) => {
        if (!cancelled) setInviteUrl(res?.data?.discordServerInviteUrl || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await api.getDiscordVerificationStatus();
      setVerificationStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading verification status:', error);
      return null;
    }
  };

  const syncVerifiedUser = (status) => {
    if (onUpdate && status?.isVerified) {
      onUpdate({
        discord_verified_at: status.verifiedAt,
        discord_username: user?.discord_username,
        discord_id: status.discordId
      });
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleStartVerification = async () => {
    const discordToVerify = user?.discord_username?.trim() || '';
    if (!discordToVerify) {
      showMessage('Add a Discord username above before starting verification', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.startDiscordVerification(discordToVerify);
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to start verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteVerification = async () => {
    if (!verificationCode.trim()) {
      showMessage('Please enter the verification code', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.completeDiscordVerification(verificationCode);
      showMessage(response.data.message, 'success');
      setVerificationCode('');
      const status = await loadVerificationStatus();
      syncVerifiedUser(status);
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to complete verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    setLoading(true);
    try {
      const response = await api.cancelDiscordVerification();
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isVerified = verificationStatus?.isVerified;
  const hasPendingVerification = verificationStatus?.hasPendingVerification;
  const isConnected = Boolean(
    isVerified
    || (user?.discord_verified_at && user.discord_verified_at !== 'undefined')
  );
  const showJoinLink = Boolean(inviteUrl) && !isConnected;
  const containerClass = isInline ? `${styles.discordVerification} ${styles.inline}` : styles.discordVerification;

  return (
    <div className={containerClass}>
      {!isInline && <h4>Discord Account Verification</h4>}

      {showJoinLink && (
        <div className={styles.joinServer}>
          <p>
            Join the LOTW Discord server first so the bot can DM you a verification code.
            You can come back here after installing or creating an account.
          </p>
          <a
            className={styles.joinLink}
            href={inviteUrl}
            target="_blank"
            rel="noreferrer"
          >
            Join the LOTW server
          </a>
        </div>
      )}

      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          {message}
        </div>
      )}

      {isVerified ? (
        <div className={styles.verifiedStatus}>
          <div className={styles.statusIcon}>✅</div>
          <div className={styles.statusText}>
            <strong>Discord verified</strong>
            <p>
              {user?.discord_username || verificationStatus?.discordUsername} is linked.
              Enable Discord pick reminders in Weekly Lock Reminders below.
            </p>
            {verificationStatus.discordId && (
              <p className={styles.discordId}>Discord ID: {verificationStatus.discordId}</p>
            )}
          </div>
        </div>
      ) : hasPendingVerification ? (
        <div className={styles.pendingVerification}>
          <div className={styles.statusIcon}>⏳</div>
          <div className={styles.statusText}>
            <strong>Verification pending</strong>
            <p>
              Check DMs from the LOTW bot for a code sent to {user?.discord_username || verificationStatus?.discordUsername}.
            </p>
          </div>

          <div className={styles.verificationForm}>
            <label htmlFor="discord-verification-code">Verification code:</label>
            <input
              id="discord-verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="6-digit code"
              maxLength="6"
              disabled={loading}
            />
            <div className={styles.buttonGroup}>
              <button
                onClick={handleCompleteVerification}
                disabled={loading || !verificationCode.trim()}
                className={styles.primaryButton}
              >
                {loading ? 'Verifying...' : 'Complete Verification'}
              </button>
              <button
                onClick={handleCancelVerification}
                disabled={loading}
                className={styles.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.notVerified}>
          <div className={styles.statusIcon}>💬</div>
          <div className={styles.statusText}>
            <strong>Discord not verified</strong>
            <p>
              {user?.discord_username
                ? `We'll DM a code to ${user.discord_username}. Change the username above first if needed.`
                : 'Add a Discord username above, then start verification for Discord pick reminders.'}
            </p>
          </div>

          <div className={styles.verificationForm}>
            <button
              onClick={handleStartVerification}
              disabled={loading || !user?.discord_username?.trim()}
              className={styles.primaryButton}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        </div>
      )}

      {!isInline && (
        <div className={styles.helpText}>
          <p><strong>How it works:</strong></p>
          <ol>
            <li>Save your Discord username in Profile above</li>
            <li>Click &quot;Send Verification Code&quot; — the bot will DM you a 6-digit code</li>
            <li>Enter the code to verify</li>
            <li>Enable Discord reminders in Weekly Lock Reminders</li>
          </ol>
          <p><em>Note: The bot must be able to send you DMs. Allow DMs from server members in Discord privacy settings.</em></p>
        </div>
      )}
    </div>
  );
};

DiscordVerification.propTypes = {
  user: PropTypes.object,
  onUpdate: PropTypes.func,
  variant: PropTypes.oneOf(['standalone', 'inline'])
};

export default DiscordVerification;
